import { Fragment } from "react";

import { Currency } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";

import { FieldRequestFieldEnum } from "@/features/common/enums";
import type { IFieldRequest } from "@/features/lender-responses/types/field-request";
import type { IFieldRequestInput } from "@/features/lender-responses/types/lender-response";
import { ucFirst } from "@/utils";
import { FIELD_REQUESTS_LABELS } from "@/utils/fieldRequests/fieldRequests";

const getNumberOfGuarantorsText = (numberOfGuarantors: number) => {
  if (numberOfGuarantors > 1) {
    return `${numberOfGuarantors} guarantors required`;
  }
  if (numberOfGuarantors === 1) {
    return "1 guarantor required";
  }

  return null;
};

const getReducedGuarantorPercentageText = (
  reducedGuarantorPercentTo: number,
) => {
  if (reducedGuarantorPercentTo < 100) {
    return `${reducedGuarantorPercentTo}% guarantee requirement`;
  }

  return null;
};

const getGuarantorCommitmentAmountText = (
  amount: number,
  currency: Currency,
): string => {
  const unit = currency === Currency.EUR ? "€" : "kr";
  return `Commitment amount: ${amount} ${unit}`;
};

const LenderResponseFieldRequestDetails = ({
  title,
  fieldRequests,
  currency,
}: {
  title: string;
  fieldRequests: (IFieldRequest | IFieldRequestInput)[];
  currency: Currency;
}) => {
  const renderField = ({
    config,
    field,
  }: IFieldRequestInput | IFieldRequest) => {
    const {
      numberOfRequiredGuarantors = 0,
      reducedGuarantorPercentTo = 100,
      guarantorCommitmentAmount = null,
    } = config || {};

    if (field === FieldRequestFieldEnum.guarantors) {
      const text = "Guarantor";

      const numberOfGuarantorsText = getNumberOfGuarantorsText(
        numberOfRequiredGuarantors || 0,
      );

      const commitmentText =
        guarantorCommitmentAmount != null
          ? getGuarantorCommitmentAmountText(
              guarantorCommitmentAmount,
              currency,
            )
          : getReducedGuarantorPercentageText(reducedGuarantorPercentTo || 100);

      if (numberOfGuarantorsText && commitmentText) {
        return `${text} (${commitmentText}, ${numberOfGuarantorsText})`;
      }
      if (numberOfGuarantorsText) {
        return `${text} (${numberOfGuarantorsText})`;
      }
      if (commitmentText) {
        return `${text} (${commitmentText})`;
      }

      return text;
    }

    return (
      FIELD_REQUESTS_LABELS[field as keyof typeof FIELD_REQUESTS_LABELS] ||
      ucFirst(field.replace(/_+/g, " "))
    );
  };

  return (
    <Fragment>
      <h5 className="mb-2 text-subsection">{title}</h5>
      <ul className="m-0 list-none p-0">
        {Array.isArray(fieldRequests) &&
          fieldRequests.map((item, index) => (
            <li
              key={"id" in item ? item.id : index}
              className={cn({
                "mb-[10px] border-b-2 border-dotted pb-[10px]":
                  index !== fieldRequests.length - 1,
              })}
            >
              <div className="flex max-w-[355px] text-sm">
                {renderField(item)}
              </div>

              {item.lenderComment && (
                <div className="mb-2 mt-1 text-sm">
                  <span className="text-helper">{item.lenderComment}</span>
                </div>
              )}
            </li>
          ))}
      </ul>
    </Fragment>
  );
};

export default LenderResponseFieldRequestDetails;
